/**
 * Sculpt-mode operations slice for the Zustand store.
 *
 * Sculpt now deforms the authored mesh directly. `baseVertices` remains as an
 * immutable reference mesh for downstream systems that need a pre-sculpt shape
 * (notably skinned mannequin rendering and topology-changing edit ops).
 *
 * Performance-critical brushes still run through the worker + WASM path.
 * Grab stays on the main thread because it relies on locked stroke state.
 */

import { getCollectionForObject } from "../app/collectionGraph.ts";
import { patchSceneObject } from "../app/sceneObjectMap.ts";
import type {
  AppState,
  InteractionMode,
  SculptBrushType,
  SculptDelta,
} from "../app/storeTypes.ts";
import { undoManager } from "../app/storeUndo.ts";
import {
  recomputeLoopNormalsPartial,
  resolveAutoSmoothAngle,
} from "../modeling/meshOps.ts";
import type { SceneObject, VertexNormals } from "../modeling/types.ts";
import {
  asVertexNormals,
  cloneLoopNormals,
  isLightObject,
  isMannequinObject,
} from "../modeling/types.ts";
import {
  applyGrab as wasmApplyGrab,
  initGrab as wasmInitGrab,
  loopToVertexNormals,
} from "../wasm/sculpt.ts";
import type {
  SculptWorkerApply,
  SculptWorkerInit,
  SculptWorkerResult,
  SculptWorkerResume,
} from "./sculptWorker.ts";
import { SpatialGrid, VertexBitset } from "./spatialIndex.ts";

type Set_ = (partial: Partial<AppState>) => void;
type Get_ = () => AppState;

const EMPTY_F32 = new Float32Array(0);
const INTERNAL_SCULPT_DEFAULTS = {
  spacing: 0.1,
  falloffCurve: "smooth" as const,
  lazyRadius: 0,
  autoSmooth: 0,
  backfaceCull: true,
};

// ---------------------------------------------------------------------------
// Module-level caches — rebuilt on stroke start, reused across samples
// ---------------------------------------------------------------------------

let _spatialGrid: SpatialGrid | null = null;
let _cachedObjectId: string | null = null;
let _verticesBuf: Float32Array | null = null;
let _vertexNormals: VertexNormals | null = null;
let _strokeActive = false;

let _grabState: {
  indices: number[];
  weights: Float32Array;
  initialPositions: Float32Array;
} | null = null;

// ---------------------------------------------------------------------------
// Sparse sculpt-delta capture
// ---------------------------------------------------------------------------

let _strokeAffectedBits: VertexBitset | null = null;
let _strokeAffectedList: number[] | null = null;
let _deltaObjectId: string | null = null;
let _beforeVertices: Float32Array | null = null;
let _lazySourceVertices: Float32Array | null = null;

function recordAffected(indices: number[]): void {
  if (!_strokeAffectedBits || !_strokeAffectedList || !_beforeVertices) return;
  const source = _lazySourceVertices;
  if (!source) return;

  for (const vi of indices) {
    if (_strokeAffectedBits.setAndTest(vi)) continue;
    _strokeAffectedList.push(vi);
    const off = vi * 3;
    _beforeVertices[off] = source[off];
    _beforeVertices[off + 1] = source[off + 1];
    _beforeVertices[off + 2] = source[off + 2];
  }
}

function buildSculptDelta(afterVertices: Float32Array): SculptDelta | null {
  if (!_strokeAffectedList?.length || !_deltaObjectId || !_beforeVertices) {
    return null;
  }

  const sorted = _strokeAffectedList.slice().sort((a, b) => a - b);
  const affected = new Uint32Array(sorted);
  const before = new Float32Array(sorted.length * 3);
  const after = new Float32Array(sorted.length * 3);

  for (let i = 0; i < sorted.length; i++) {
    const vi = sorted[i];
    const off = vi * 3;
    const out = i * 3;
    before[out] = _beforeVertices[off];
    before[out + 1] = _beforeVertices[off + 1];
    before[out + 2] = _beforeVertices[off + 2];
    after[out] = afterVertices[off];
    after[out + 1] = afterVertices[off + 1];
    after[out + 2] = afterVertices[off + 2];
  }

  return {
    type: "sculpt-delta",
    objectId: _deltaObjectId,
    affected,
    beforeVertices: before,
    afterVertices: after,
  };
}

function clearDeltaState(): void {
  if (_strokeAffectedBits) _strokeAffectedBits.clear();
  _strokeAffectedList = null;
  _deltaObjectId = null;
  _beforeVertices = null;
  _lazySourceVertices = null;
}

// ---------------------------------------------------------------------------
// Dirty ranges consumed by the renderer for partial uploads
// ---------------------------------------------------------------------------

const _dirtyVertexList: number[] = [];
let _dirtyVertexBits: VertexBitset | null = null;
let _dirtyCornerMin = Infinity;
let _dirtyCornerMax = -1;

function markDirty(indices: number[]): void {
  if (!_dirtyVertexBits) return;
  for (const vi of indices) {
    if (!_dirtyVertexBits.setAndTest(vi)) _dirtyVertexList.push(vi);
  }
}

function mergeCornerRange(minCorner: number, maxCorner: number): void {
  if (minCorner < _dirtyCornerMin) _dirtyCornerMin = minCorner;
  if (maxCorner > _dirtyCornerMax) _dirtyCornerMax = maxCorner;
}

export function consumeDirtySculptUpdate(): {
  vertices: number[];
  cornerMin: number;
  cornerMax: number;
} | null {
  if (_dirtyVertexList.length === 0) return null;
  const result = {
    vertices: _dirtyVertexList.slice(),
    cornerMin: _dirtyCornerMin,
    cornerMax: _dirtyCornerMax,
  };
  _dirtyVertexList.length = 0;
  if (_dirtyVertexBits) _dirtyVertexBits.clear();
  _dirtyCornerMin = Infinity;
  _dirtyCornerMax = -1;
  return result;
}

// ---------------------------------------------------------------------------
// Worker pipeline
// ---------------------------------------------------------------------------

let _sculptWorker: Worker | null = null;
let _workerBusy = false;
let _workerPending: SculptWorkerApply | null = null;
let _workerInitialised = false;
let _workerGet: Get_ | null = null;
let _workerSet: Set_ | null = null;
let _workerObjectId: string | null = null;
let _workerCachedIndices: Uint32Array | null = null;
let _finishStrokeRequested = false;

function getSculptWorker(): Worker {
  if (!_sculptWorker) {
    _sculptWorker = new Worker(new URL("./sculptWorker.ts", import.meta.url), {
      type: "module",
    });
    _sculptWorker.onmessage = handleSculptWorkerMessage;
  }
  return _sculptWorker;
}

function flushPendingWorkerDab(): void {
  if (_workerPending && !_workerBusy && _workerInitialised) {
    const msg = _workerPending;
    _workerPending = null;
    _workerBusy = true;
    getSculptWorker().postMessage(msg);
  }
}

function maybeFinalizeDeferredStroke(): void {
  if (
    !_finishStrokeRequested ||
    _workerBusy ||
    _workerPending ||
    !_workerGet ||
    !_workerSet
  ) {
    return;
  }
  _finishStrokeRequested = false;
  finalizeStroke(_workerSet, _workerGet);
}

function handleSculptWorkerMessage(
  e: MessageEvent<SculptWorkerResult | { type: "ready" }>,
): void {
  if (e.data.type === "ready") {
    _workerInitialised = true;
    _workerBusy = false;
    flushPendingWorkerDab();
    maybeFinalizeDeferredStroke();
    return;
  }

  _workerBusy = false;
  const result = e.data as SculptWorkerResult;

  if (_workerGet && _workerObjectId && result.affected.length > 0) {
    const obj = _workerGet().sceneObjects.get(_workerObjectId);
    if (obj) {
      recordAffected(result.affected);

      for (let i = 0; i < result.affected.length; i++) {
        const off = result.affected[i] * 3;
        const src = i * 3;
        obj.vertices[off] = result.positions[src];
        obj.vertices[off + 1] = result.positions[src + 1];
        obj.vertices[off + 2] = result.positions[src + 2];
      }

      const { minCorner, maxCorner } = recomputeLoopNormalsPartial(
        obj.vertices,
        obj.loopNormals,
        result.affected,
        obj.topology,
        resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
      );
      mergeCornerRange(minCorner, maxCorner);
      markDirty(result.affected);
    }

    if (_verticesBuf) {
      for (let i = 0; i < result.affected.length; i++) {
        const off = result.affected[i] * 3;
        const src = i * 3;
        _verticesBuf[off] = result.positions[src];
        _verticesBuf[off + 1] = result.positions[src + 1];
        _verticesBuf[off + 2] = result.positions[src + 2];
      }
      if (_spatialGrid) _spatialGrid.updateVertices(result.affected, _verticesBuf);
    }
  }

  flushPendingWorkerDab();
  maybeFinalizeDeferredStroke();
}

function clearWorkerState(): void {
  _workerBusy = false;
  _workerPending = null;
  _workerInitialised = false;
  _workerObjectId = null;
  _finishStrokeRequested = false;
}

const MAIN_THREAD_BRUSHES = new Set<SculptBrushType>(["grab"]);

// ---------------------------------------------------------------------------
// Shared stroke helpers
// ---------------------------------------------------------------------------

function ensureBuffers(vertexCount3: number): void {
  const vc = vertexCount3 / 3;
  if (!_verticesBuf || _verticesBuf.length !== vertexCount3) {
    _verticesBuf = new Float32Array(vertexCount3);
  }
  if (!_vertexNormals || _vertexNormals.length !== vertexCount3) {
    _vertexNormals = asVertexNormals(new Float32Array(vertexCount3));
  }
  if (!_strokeAffectedBits) {
    _strokeAffectedBits = new VertexBitset(vc);
  } else {
    _strokeAffectedBits.ensure(vc);
  }
  if (!_dirtyVertexBits) {
    _dirtyVertexBits = new VertexBitset(vc);
  } else {
    _dirtyVertexBits.ensure(vc);
  }
}

function ensureCaches(state: AppState): void {
  const sm = state.sculptMode;
  if (!sm) return;
  const obj = state.sceneObjects.get(sm.objectId);
  if (!obj) return;

  if (_cachedObjectId !== sm.objectId || !_spatialGrid) {
    const verts = _strokeActive && _verticesBuf ? _verticesBuf : obj.vertices;
    _spatialGrid = new SpatialGrid(verts, 0.2);
    _cachedObjectId = sm.objectId;
  }
}

function clearCaches(): void {
  _spatialGrid = null;
  _cachedObjectId = null;
  _verticesBuf = null;
  _vertexNormals = null;
  _grabState = null;
  _strokeActive = false;
  _strokeAffectedBits = null;
  _strokeAffectedList = null;
  _dirtyVertexList.length = 0;
  _dirtyVertexBits = null;
  _dirtyCornerMin = Infinity;
  _dirtyCornerMax = -1;
  clearDeltaState();
}

function filterFrontFacing(
  indices: number[],
  normals: VertexNormals,
  viewDirection: [number, number, number],
): number[] {
  const result: number[] = [];
  const [vx, vy, vz] = viewDirection;
  for (const vi of indices) {
    const off = vi * 3;
    const dot =
      normals[off] * vx + normals[off + 1] * vy + normals[off + 2] * vz;
    if (dot < 0.2) result.push(vi);
  }
  return result;
}

function ensureBaseVerticesForSculpt(
  objectId: string,
  obj: SceneObject,
  set: Set_,
  get: Get_,
): void {
  if (obj.baseVertices) return;
  set({
    sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
      baseVertices: obj.vertices.slice(),
    }),
  });
}

function beginStroke(
  sm: NonNullable<AppState["sculptMode"]>,
  obj: SceneObject,
  strokeRadius: number,
): void {
  const len = obj.vertices.length;
  ensureBuffers(len);
  _verticesBuf!.set(obj.vertices);
  _vertexNormals!.set(
    loopToVertexNormals(obj.loopNormals, obj.topology, obj.vertices.length / 3),
  );
  _strokeActive = true;
  _strokeAffectedList = [];
  _strokeAffectedBits!.clear();
  if (!_beforeVertices || _beforeVertices.length < len) {
    _beforeVertices = new Float32Array(len);
  }
  _lazySourceVertices = obj.vertices;
  _deltaObjectId = sm.objectId;
  if (_spatialGrid) _spatialGrid.setCellSize(Math.max(0.01, strokeRadius * 2));
}

function finalizeStroke(set: Set_, get: Get_): void {
  const state = get();
  const sm = state.sculptMode;
  _grabState = null;

  if (sm && _strokeActive && _verticesBuf) {
    const obj = state.sceneObjects.get(sm.objectId);
    if (obj) {
      const finalVertices = _verticesBuf;
      _verticesBuf = null;
      _vertexNormals = null;

      const delta = buildSculptDelta(finalVertices);

      set({
        sceneObjects: patchSceneObject(get().sceneObjects, sm.objectId, {
          vertices: finalVertices,
          loopNormals: cloneLoopNormals(obj.loopNormals),
        }),
        sculptStrokeSeq: get().sculptStrokeSeq + 1,
      });

      if (delta) {
        undoManager.push(delta);
        set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
      }
    }
  } else {
    set({ sculptStrokeSeq: get().sculptStrokeSeq + 1 });
  }

  _strokeActive = false;
  clearDeltaState();
}

// ---------------------------------------------------------------------------
// Store operations
// ---------------------------------------------------------------------------

export function createSculptOps(set: Set_, get: Get_) {
  _workerGet = get;
  _workerSet = set;

  return {
    enterSculptMode: (objectId: string) => {
      const obj = get().sceneObjects.get(objectId);
      const collection = getCollectionForObject(get().collections, objectId);
      if (
        !obj ||
        isLightObject(obj) ||
        isMannequinObject(obj) ||
        obj.generator ||
        collection?.generatorRef
      ) {
        return;
      }

      set({
        sculptMode: {
          objectId,
          brush: "draw",
          radius: 50,
          strength: 0.5,
          invert: false,
          ...INTERNAL_SCULPT_DEFAULTS,
        },
        mode: "navigate" as InteractionMode,
        selectedObjectTargets: [{ kind: "sceneObject", id: objectId }],
        editMode: null,
        poseMode: null,
        workspaceMode: "sculpt",
      });
    },

    exitSculptMode: () => {
      clearCaches();
      clearWorkerState();
      set({
        sculptMode: null,
        mode: "transform" as InteractionMode,
        workspaceMode: "object",
      });
    },

    setSculptBrush: (brush: SculptBrushType) => {
      const sm = get().sculptMode;
      if (!sm) return;
      set({ sculptMode: { ...sm, brush } });
    },

    setSculptRadius: (radius: number) => {
      const sm = get().sculptMode;
      if (!sm) return;
      set({ sculptMode: { ...sm, radius: Math.max(1, radius) } });
    },

    setSculptStrength: (strength: number) => {
      const sm = get().sculptMode;
      if (!sm) return;
      set({
        sculptMode: { ...sm, strength: Math.max(0, Math.min(1, strength)) },
      });
    },

    setSculptInvert: (invert: boolean) => {
      const sm = get().sculptMode;
      if (!sm) return;
      set({ sculptMode: { ...sm, invert } });
    },

    initGrabSculptStroke: (
      center: [number, number, number],
      objectLocalRadius?: number,
      viewDirectionLocal?: [number, number, number],
    ) => {
      const state = get();
      const sm = state.sculptMode;
      if (!sm) return;

      let obj = state.sceneObjects.get(sm.objectId);
      if (!obj) return;
      ensureBaseVerticesForSculpt(sm.objectId, obj, set, get);
      obj = get().sceneObjects.get(sm.objectId) ?? obj;

      ensureCaches(get());
      if (!_spatialGrid) return;

      const radius = objectLocalRadius ?? sm.radius;
      beginStroke(sm, obj, radius);

      let affected = _spatialGrid
        .queryRadius(center[0], center[1], center[2], radius)
        .slice();
      if (sm.backfaceCull && viewDirectionLocal) {
        affected = filterFrontFacing(affected, _vertexNormals!, viewDirectionLocal);
      }

      if (affected.length === 0) {
        _strokeActive = false;
        clearDeltaState();
        _grabState = null;
        return;
      }

      const weights = wasmInitGrab(
        _verticesBuf!,
        new Uint32Array(affected),
        center,
        radius,
        sm.strength,
        sm.falloffCurve,
        EMPTY_F32,
      );

      const initialPositions = new Float32Array(affected.length * 3);
      for (let i = 0; i < affected.length; i++) {
        const off = affected[i] * 3;
        const out = i * 3;
        initialPositions[out] = _verticesBuf![off];
        initialPositions[out + 1] = _verticesBuf![off + 1];
        initialPositions[out + 2] = _verticesBuf![off + 2];
      }

      _grabState = { indices: affected, weights, initialPositions };
    },

    applySculptStroke: (params: {
      center: [number, number, number];
      pressure: number;
      normal: [number, number, number];
      grabTotalDelta?: [number, number, number];
      radius?: number;
      viewDirectionLocal?: [number, number, number];
      strengthMultiplier?: number;
    }) => {
      const sm = get().sculptMode;
      if (!sm) return;

      let obj = get().sceneObjects.get(sm.objectId);
      if (!obj) return;
      ensureBaseVerticesForSculpt(sm.objectId, obj, set, get);
      obj = get().sceneObjects.get(sm.objectId) ?? obj;

      ensureCaches(get());
      if (!_spatialGrid) return;

      const strokeRadius = params.radius ?? sm.radius;
      const effectiveStrength = sm.strength * (params.strengthMultiplier ?? 1);

      if (!_strokeActive) {
        beginStroke(sm, obj, strokeRadius);

        if (!MAIN_THREAD_BRUSHES.has(sm.brush)) {
          clearWorkerState();
          _workerObjectId = sm.objectId;
          const cellSize = Math.max(0.01, strokeRadius * 2);
          const canResume =
            _workerCachedIndices === obj.indices && _workerInitialised;

          if (canResume) {
            const resumeMsg: SculptWorkerResume = {
              type: "resume",
              vertices: obj.vertices,
              loopNormals: obj.loopNormals,
              cellSize,
            };
            _workerBusy = true;
            getSculptWorker().postMessage(resumeMsg);
          } else {
            const initMsg: SculptWorkerInit = {
              type: "init",
              vertices: obj.vertices,
              loopNormals: obj.loopNormals,
              topology: obj.topology,
              cellSize,
              autoSmoothAngle: resolveAutoSmoothAngle(
                obj.shading,
                obj.autoSmoothAngle,
              ),
            };
            _workerBusy = true;
            getSculptWorker().postMessage(initMsg);
            _workerCachedIndices = obj.indices;
          }
        }
      }

      if (sm.brush === "grab") {
        if (!_grabState || !params.grabTotalDelta) return;
        const positions = wasmApplyGrab(
          _grabState.weights,
          _grabState.initialPositions,
          params.grabTotalDelta[0],
          params.grabTotalDelta[1],
          params.grabTotalDelta[2],
        );
        const affected = _grabState.indices;

        for (let i = 0; i < affected.length; i++) {
          const off = affected[i] * 3;
          const src = i * 3;
          _verticesBuf![off] = positions[src];
          _verticesBuf![off + 1] = positions[src + 1];
          _verticesBuf![off + 2] = positions[src + 2];
        }

        const { minCorner, maxCorner } = recomputeLoopNormalsPartial(
          _verticesBuf!,
          obj.loopNormals,
          affected,
          obj.topology,
          resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
        );
        mergeCornerRange(minCorner, maxCorner);
        _spatialGrid.updateVertices(affected, _verticesBuf!);

        recordAffected(affected);
        for (const vi of affected) {
          const off = vi * 3;
          obj.vertices[off] = _verticesBuf![off];
          obj.vertices[off + 1] = _verticesBuf![off + 1];
          obj.vertices[off + 2] = _verticesBuf![off + 2];
        }
        markDirty(affected);
        return;
      }

      const applyMsg: SculptWorkerApply = {
        type: "apply",
        brush: sm.brush,
        center: params.center,
        normal: params.normal,
        radius: strokeRadius,
        strength: effectiveStrength,
        pressure: params.pressure,
        invert: sm.invert,
        falloffCurve: sm.falloffCurve,
        autoSmooth: sm.autoSmooth,
        backfaceCull: sm.backfaceCull,
        viewDirectionLocal: params.viewDirectionLocal,
      };

      if (_workerBusy) {
        _workerPending = applyMsg;
      } else {
        _workerBusy = true;
        getSculptWorker().postMessage(applyMsg);
      }
    },

    endSculptStroke: () => {
      if (_workerBusy || _workerPending) {
        _finishStrokeRequested = true;
        return;
      }
      finalizeStroke(set, get);
    },
  };
}
